package ai.jessie.web.customers

import ai.jessie.shared.customers.CustomerMatchResponse
import ai.jessie.shared.customers.CustomerResponse
import ai.jessie.shared.customers.SaveCustomerRequest
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.DefinitionsStyle
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.StackStyle
import ai.jessie.web.theme.SubListStyle
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement

// Step 7, A4 and A8: the exact proposal the server validated, as a step inside the slide-over.
// Nothing is saved until the member confirms in the footer; cancelling (A12) sends nothing.
@Composable
internal fun customerReview(state: CustomerUiState, actions: CustomerActions) {
    val proposal = state.pending ?: return
    val duplicates = state.preview?.duplicates.orEmpty()
    Div(
        attrs =
            StackStyle.toAttrs {
                id(REVIEW_ID)
                attr("role", "alertdialog")
                attr("aria-labelledby", CUSTOMER_REVIEW_TITLE_ID)
                attr("tabindex", "-1")
            }
    ) {
        proposedRecord(proposal)
        state.current?.let { removedChildren(it, proposal) }
        duplicateWarning(duplicates, state, actions)
    }
    LaunchedEffect(state.preview) {
        if (!AppGlobals.isExporting) (document.getElementById(REVIEW_ID) as? HTMLElement)?.focus()
    }
}

/** The slide-over heading of the review step; the review dialog is labelled by it. */
@Composable
internal fun customerReviewHeading(state: CustomerUiState) {
    val proposal = state.pending ?: return
    H2(attrs = { id(CUSTOMER_REVIEW_TITLE_ID) }) { Text(reviewTitle(state.current, proposal)) }
}

private fun reviewTitle(current: CustomerResponse?, proposal: SaveCustomerRequest) =
    when {
        current == null -> "Neuen Kunden prüfen und bestätigen"
        current.status != proposal.status && proposal.status == "ARCHIVED" ->
            "Archivierung prüfen und bestätigen"
        current.status != proposal.status -> "Reaktivierung prüfen und bestätigen"
        else -> "Änderungen prüfen und bestätigen"
    }

@Composable
private fun proposedRecord(proposal: SaveCustomerRequest) {
    TagElement<HTMLElement>("dl", DefinitionsStyle.toAttrs()) {
        definition("Kundentyp") { Text(customerTypes[proposal.type] ?: "–") }
        definition("Name") { Text(proposal.displayName) }
        definition("Status") { statusBadge(proposal.status) }
        definition("E-Mail") { Text(proposal.email ?: "–") }
        definition("Telefon") { Text(proposal.phone ?: "–") }
        definition("Adressen") {
            subList(proposal.addresses.map { it.summary() }, "Keine Adressen.")
        }
        definition("Kontaktpersonen") {
            subList(proposal.contacts.map { it.summary() }, "Keine Kontaktpersonen.")
        }
    }
    if (proposal.status == "ARCHIVED")
        P(attrs = HintStyle.toAttrs()) {
            Text("Archivieren erhält alle bestehenden Verknüpfungen des Kunden.")
        }
}

// A confirmed full-record save removes stored children that the proposal no longer contains.
@Composable
private fun removedChildren(current: CustomerResponse, proposal: SaveCustomerRequest) {
    val addresses = current.addresses.filter { old -> proposal.addresses.none { it.id == old.id } }
    val contacts =
        current.contacts
            .map { it.details }
            .filter { old -> proposal.contacts.none { it.id == old.id } }
    if (addresses.isEmpty() && contacts.isEmpty()) return
    H3 { Text("Wird entfernt") }
    Ul(attrs = SubListStyle.toAttrs()) {
        addresses.forEach { Li { Text(it.summary()) } }
        contacts.forEach { Li { Text(it.summary()) } }
    }
}

// A7, BR-071: only the matches the server returned for this organisation.
@Composable
private fun duplicateWarning(
    duplicates: List<CustomerMatchResponse>,
    state: CustomerUiState,
    actions: CustomerActions,
) {
    if (duplicates.isEmpty()) return
    Div(attrs = listOf(AlertStyle, StackStyle).toAttrs()) {
        H3 { Text("Mögliche Dubletten") }
        P { Text("Name, E-Mail oder Postadresse stimmen mit bestehenden Kunden überein:") }
        Ul(attrs = SubListStyle.toAttrs()) { duplicates.forEach { Li { Text(it.displayName) } } }
        customerCheckbox(
            "duplicateConfirmed",
            "Ich bestätige, dass dieser Kunde eigenständig ist.",
            state.duplicateConfirmed,
            actions.confirmDistinct,
        )
    }
}

internal const val CUSTOMER_REVIEW_TITLE_ID = "customer-review-title"
private const val REVIEW_ID = "customer-review"
